import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError } from '@/api/client';
import { Permission } from '@/auth/permissions';
import { useAuth } from '@/auth/useAuth';
import { SpriteIcon } from '@/components/SpriteIcon';
import { useConfirm } from '@/components/ConfirmDialog';
import { useRoles } from './useRoles';
import {
  buildMenuTree,
  menuItemRoles,
  toRequiredRoles,
  useCreateMenuItem,
  useDeleteMenuItem,
  useMenuItems,
  useToggleMenuItemActive,
  useUpdateMenuItem,
  type MenuItem,
  type MenuItemInput,
} from './useMenuItems';

/**
 * Replaces `Pages/Admin/MenuManagement.cshtml` at `/admin/menu-management`.
 *
 * Two panels, as the Razor page had: the menu tree on the left, the editor for
 * the selected entry on the right. The tree is built client-side from the flat
 * list — see `buildMenuTree` for why the API's own `children` is not trusted.
 */

const BADGE_STYLES = [
  { value: '', labelKey: 'Menus.Badge.Default', fallback: 'Default' },
  { value: 'bg-success text-white', labelKey: 'Menus.Badge.Success', fallback: 'Success (green)' },
  { value: 'bg-warning text-dark', labelKey: 'Menus.Badge.Warning', fallback: 'Warning (yellow)' },
  { value: 'bg-danger text-white', labelKey: 'Menus.Badge.Danger', fallback: 'Danger (red)' },
  { value: 'bg-info text-white', labelKey: 'Menus.Badge.Info', fallback: 'Info (blue)' },
];

const EMPTY_FORM: MenuItemInput = {
  title: '',
  titleKey: null,
  url: null,
  icon: null,
  requiredRoles: null,
  displayOrder: 1,
  parentId: null,
  isTitle: false,
  isActive: true,
  cssClass: null,
  target: null,
  badgeText: null,
  badgeCssClass: null,
  dataFilterTags: null,
};

/** Empty inputs are stored as null, not '', so the column stays clean. */
function orNull(value: string): string | null {
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function errorText(error: unknown): string | null {
  if (!(error instanceof ApiError)) return null;
  const problem = error.problem as { message?: string } | null;
  return problem?.message ?? error.message;
}

interface TreeNodeProps {
  item: MenuItem;
  depth: number;
  selectedId: number | null;
  onSelect: (item: MenuItem) => void;
  onAddChild: (parent: MenuItem) => void;
}

function TreeNode({ item, depth, selectedId, onSelect, onAddChild }: TreeNodeProps) {
  const { t } = useTranslation();
  const children = item.children ?? [];

  return (
    <>
      <div
        // No `px-2` here: Bootstrap's spacing utilities compile with
        // `!important`, so the class would beat the inline padding and every
        // level would sit flush left — the nesting was correct but invisible.
        // Both sides of the padding are set inline instead.
        className={`d-flex align-items-center gap-2 py-1 rounded${
          selectedId === item.id ? ' bg-primary-subtle' : ''
        }`}
        style={{ paddingLeft: `${depth * 1.25 + 0.5}rem`, paddingRight: '0.5rem' }}
      >
        <button
          type="button"
          className="btn btn-link btn-sm text-decoration-none text-start flex-grow-1 p-0"
          onClick={() => onSelect(item)}
        >
          <span className={item.isActive ? '' : 'text-muted text-decoration-line-through'}>
            {item.isTitle ? <em>{item.title}</em> : item.title}
          </span>
          {item.badgeText && (
            <span className={`badge ms-2 ${item.badgeCssClass || 'bg-secondary'}`}>
              {item.badgeText}
            </span>
          )}
        </button>
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary py-0 px-1"
          onClick={() => onAddChild(item)}
          title={t('Menus.AddChild', 'Add child item')}
          aria-label={t('Menus.AddChild', 'Add child item')}
        >
          <SpriteIcon name="plus" size={12} />
        </button>
      </div>

      {children.map((child) => (
        <TreeNode
          key={child.id}
          item={child}
          depth={depth + 1}
          selectedId={selectedId}
          onSelect={onSelect}
          onAddChild={onAddChild}
        />
      ))}
    </>
  );
}

export function MenuManagementPage() {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const confirm = useConfirm();

  const can = {
    create: hasPermission(Permission.menus.create),
    edit: hasPermission(Permission.menus.edit),
    delete: hasPermission(Permission.menus.delete),
  };

  const { data: items, isLoading } = useMenuItems();
  const { data: rolesData } = useRoles();
  const createItem = useCreateMenuItem();
  const updateItem = useUpdateMenuItem();
  const deleteItem = useDeleteMenuItem();
  const toggleActive = useToggleMenuItemActive();

  const tree = useMemo(() => buildMenuTree(items ?? []), [items]);
  const availableRoles = rolesData?.roles.map((role) => role.name) ?? [];

  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<MenuItemInput | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // Re-read the selected entry after a refetch so the form shows what was saved,
  // but only while its identity is unchanged — never clobber an in-progress edit
  // for a different row.
  useEffect(() => {
    if (editingId == null || !items) return;
    const fresh = items.find((item) => item.id === editingId);
    if (!fresh) setEditingId(null);
  }, [items, editingId]);

  const selectItem = useCallback((item: MenuItem) => {
    setEditingId(item.id);
    setForm({
      title: item.title,
      titleKey: item.titleKey,
      url: item.url,
      icon: item.icon,
      requiredRoles: item.requiredRoles,
      displayOrder: item.displayOrder,
      parentId: item.parentId,
      isTitle: item.isTitle,
      isActive: item.isActive,
      cssClass: item.cssClass,
      target: item.target,
      badgeText: item.badgeText,
      badgeCssClass: item.badgeCssClass,
      dataFilterTags: item.dataFilterTags,
    });
  }, []);

  const startCreate = useCallback((parent: MenuItem | null) => {
    setEditingId(null);
    setForm({ ...EMPTY_FORM, parentId: parent?.id ?? null });
  }, []);

  const set = useCallback(
    <K extends keyof MenuItemInput>(key: K, value: MenuItemInput[K]) =>
      setForm((current) => (current ? { ...current, [key]: value } : current)),
    [],
  );

  const selectedRoles = useMemo(() => menuItemRoles(form?.requiredRoles ?? null), [form]);

  const toggleRole = useCallback(
    (role: string) => {
      const next = selectedRoles.includes(role)
        ? selectedRoles.filter((r) => r !== role)
        : [...selectedRoles, role];
      set('requiredRoles', toRequiredRoles(next));
    },
    [selectedRoles, set],
  );

  const handleSubmit = useCallback(() => {
    if (!form) return;
    const done = (message: string) => () => {
      setNotice(message);
      setForm(null);
      setEditingId(null);
    };

    if (editingId != null) {
      updateItem.mutate(
        { ...form, id: editingId },
        { onSuccess: done(t('Menus.Updated', 'Menu item saved.')) },
      );
    } else {
      createItem.mutate(form, { onSuccess: done(t('Menus.Created', 'Menu item created.')) });
    }
  }, [form, editingId, updateItem, createItem, t]);

  const handleDelete = useCallback(async () => {
    if (editingId == null) return;
    const item = items?.find((i) => i.id === editingId);
    const hasChildren = (items ?? []).some((i) => i.parentId === editingId);

    const message = hasChildren
      ? t(
          'Menus.ConfirmDeleteWithChildren',
          'Delete "{{title}}" and all of its child items? This cannot be undone.',
          { title: item?.title ?? '' },
        )
      : t('Menus.ConfirmDelete', 'Delete "{{title}}"?', { title: item?.title ?? '' });

    const confirmed = await confirm({
      variant: 'danger',
      icon: 'trash-2',
      title: t('crud:Crud.Delete.ConfirmTitle', 'Confirm deletion'),
      message,
      confirmLabel: t('Button.Delete', 'Delete'),
    });
    if (!confirmed) return;

    // A failure keeps reporting itself through `formError`, which the panel
    // already renders for every mutation on this screen.
    deleteItem.mutate(
      { id: editingId, deleteChildren: hasChildren },
      {
        onSuccess: () => {
          setNotice(t('Menus.Deleted', 'Menu item deleted.'));
          setForm(null);
          setEditingId(null);
        },
      },
    );
  }, [editingId, items, confirm, deleteItem, t]);

  const formError = errorText(createItem.error ?? updateItem.error ?? deleteItem.error);
  const pending = createItem.isPending || updateItem.isPending || deleteItem.isPending;

  return (
    <div className="container-fluid py-3">
      <div className="d-flex justify-content-between align-items-start mb-3">
        <div>
          <h1 className="h3 mb-1">{t('Menus.Title', 'Menu management')}</h1>
          <p className="text-muted mb-0">
            {t('Menus.Subtitle', 'The navigation tree every user sees, filtered by their roles.')}
          </p>
        </div>
        {can.create && (
          <button
            type="button"
            className="btn btn-primary d-inline-flex align-items-center"
            onClick={() => startCreate(null)}
          >
            <SpriteIcon name="plus" size={16} className="sa-icon me-2" />
            {t('Menus.AddRoot', 'Add root item')}
          </button>
        )}
      </div>

      {notice && (
        <div className="alert alert-success alert-dismissible" role="alert">
          {notice}
          <button type="button" className="btn-close" onClick={() => setNotice(null)} />
        </div>
      )}

      <div className="row g-3">
        <div className="col-lg-5">
          <div className="card shadow-sm h-100">
            <div className="card-header bg-transparent">
              <h5 className="mb-0">{t('Menus.Tree', 'Menu tree')}</h5>
            </div>
            <div className="card-body">
              {isLoading && (
                <div className="text-center py-4">
                  <span className="spinner-border spinner-border-sm me-2" role="status" />
                  {t('Loading', 'Loading...')}
                </div>
              )}
              {!isLoading && tree.length === 0 && (
                <p className="text-muted text-center py-4 mb-0">
                  {t('Menus.Empty', 'No menu items defined')}
                </p>
              )}
              {tree.map((item) => (
                <TreeNode
                  key={item.id}
                  item={item}
                  depth={0}
                  selectedId={editingId}
                  onSelect={selectItem}
                  onAddChild={startCreate}
                />
              ))}
            </div>
          </div>
        </div>

        <div className="col-lg-7">
          <div className="card shadow-sm h-100">
            <div className="card-header bg-transparent">
              <h5 className="mb-0">
                {editingId != null
                  ? t('Menus.EditItem', 'Edit menu item')
                  : form
                    ? t('Menus.NewItem', 'New menu item')
                    : t('Menus.Details', 'Menu item details')}
              </h5>
            </div>
            <div className="card-body">
              {!form && (
                <p className="text-muted text-center py-5 mb-0">
                  {t('Menus.SelectPrompt', 'Select an item from the tree, or add a new one.')}
                </p>
              )}

              {form && (
                <form
                  onSubmit={(event) => {
                    event.preventDefault();
                    handleSubmit();
                  }}
                >
                  {formError && (
                    <div className="alert alert-danger" role="alert">
                      {formError}
                    </div>
                  )}

                  <div className="mb-3">
                    <label className="form-label" htmlFor="mi-title">
                      {t('Menus.ItemTitle', 'Title')} <span className="text-danger">*</span>
                    </label>
                    <input
                      id="mi-title"
                      className="form-control"
                      value={form.title}
                      onChange={(e) => set('title', e.target.value)}
                      maxLength={100}
                      required
                    />
                  </div>

                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label" htmlFor="mi-titlekey">
                        {t('Menus.TitleKey', 'Title key')}
                      </label>
                      <input
                        id="mi-titlekey"
                        className="form-control"
                        value={form.titleKey ?? ''}
                        onChange={(e) => set('titleKey', orNull(e.target.value))}
                      />
                      <div className="form-text">
                        {t('Menus.TitleKeyHint', 'Localisation key; the title is used when empty.')}
                      </div>
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label" htmlFor="mi-url">
                        {t('Menus.Url', 'URL')}
                      </label>
                      <input
                        id="mi-url"
                        className="form-control"
                        value={form.url ?? ''}
                        onChange={(e) => set('url', orNull(e.target.value))}
                        placeholder="/stla/takata"
                      />
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label" htmlFor="mi-icon">
                        {t('Menus.Icon', 'Icon')}
                      </label>
                      <input
                        id="mi-icon"
                        className="form-control"
                        value={form.icon ?? ''}
                        onChange={(e) => set('icon', orNull(e.target.value))}
                        placeholder="grid"
                      />
                      <div className="form-text">
                        {t('Menus.IconHint', 'Sprite symbol name, e.g. grid, users, shield.')}
                      </div>
                    </div>
                    <div className="col-md-3 mb-3">
                      <label className="form-label" htmlFor="mi-badge">
                        {t('Menus.BadgeText', 'Badge text')}
                      </label>
                      <input
                        id="mi-badge"
                        className="form-control"
                        value={form.badgeText ?? ''}
                        onChange={(e) => set('badgeText', orNull(e.target.value))}
                        maxLength={50}
                      />
                    </div>
                    <div className="col-md-3 mb-3">
                      <label className="form-label" htmlFor="mi-badgestyle">
                        {t('Menus.BadgeStyle', 'Badge style')}
                      </label>
                      <select
                        id="mi-badgestyle"
                        className="form-select"
                        value={form.badgeCssClass ?? ''}
                        onChange={(e) => set('badgeCssClass', orNull(e.target.value))}
                      >
                        {BADGE_STYLES.map((style) => (
                          <option key={style.value} value={style.value}>
                            {t(style.labelKey, style.fallback)}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="form-label" htmlFor="mi-tags">
                      {t('Menus.FilterTags', 'Filter tags')}
                    </label>
                    <input
                      id="mi-tags"
                      className="form-control"
                      value={form.dataFilterTags ?? ''}
                      onChange={(e) => set('dataFilterTags', orNull(e.target.value))}
                      maxLength={500}
                    />
                    <div className="form-text">
                      {t('Menus.FilterTagsHint', 'Keywords for the menu search; derived from the title when empty.')}
                    </div>
                  </div>

                  <fieldset className="mb-3">
                    <legend className="form-label fs-6">
                      {t('Menus.RequiredRoles', 'Required roles')}
                    </legend>
                    <div className="border rounded p-2">
                      {availableRoles.map((role) => (
                        <div className="form-check" key={role}>
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id={`mi-role-${role}`}
                            checked={selectedRoles.includes(role)}
                            onChange={() => toggleRole(role)}
                          />
                          <label className="form-check-label" htmlFor={`mi-role-${role}`}>
                            {role}
                          </label>
                        </div>
                      ))}
                    </div>
                    <div className="form-text">
                      {t('Menus.RolesHint', 'The user must hold at least one of these; none means everyone.')}
                    </div>
                  </fieldset>

                  <div className="row">
                    <div className="col-md-4 mb-3">
                      <label className="form-label" htmlFor="mi-order">
                        {t('Menus.DisplayOrder', 'Display order')}
                      </label>
                      <input
                        id="mi-order"
                        type="number"
                        min={1}
                        className="form-control"
                        value={form.displayOrder}
                        onChange={(e) => set('displayOrder', Number(e.target.value))}
                      />
                    </div>
                    <div className="col-md-4 mb-3">
                      <label className="form-label" htmlFor="mi-target">
                        {t('Menus.Target', 'Target')}
                      </label>
                      <select
                        id="mi-target"
                        className="form-select"
                        value={form.target ?? ''}
                        onChange={(e) => set('target', orNull(e.target.value))}
                      >
                        <option value="">{t('Menus.SameWindow', 'Same window')}</option>
                        <option value="_blank">{t('Menus.NewWindow', 'New window')}</option>
                      </select>
                    </div>
                    <div className="col-md-4 mb-3">
                      <label className="form-label" htmlFor="mi-css">
                        {t('Menus.CssClass', 'CSS class')}
                      </label>
                      <input
                        id="mi-css"
                        className="form-control"
                        value={form.cssClass ?? ''}
                        onChange={(e) => set('cssClass', orNull(e.target.value))}
                        maxLength={100}
                      />
                    </div>
                  </div>

                  <div className="form-check form-switch mb-2">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      role="switch"
                      id="mi-istitle"
                      checked={form.isTitle}
                      onChange={(e) => set('isTitle', e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="mi-istitle">
                      {t('Menus.IsTitle', 'Section heading (not clickable)')}
                    </label>
                  </div>

                  <div className="form-check form-switch mb-3">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      role="switch"
                      id="mi-isactive"
                      checked={form.isActive}
                      onChange={(e) => set('isActive', e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="mi-isactive">
                      {t('Menus.IsActive', 'Active')}
                    </label>
                  </div>

                  <div className="d-flex gap-2">
                    <button
                      type="submit"
                      className="btn btn-primary flex-fill"
                      disabled={pending || (editingId != null ? !can.edit : !can.create)}
                    >
                      {pending ? t('Saving', 'Saving…') : t('Save', 'Save')}
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => {
                        setForm(null);
                        setEditingId(null);
                      }}
                    >
                      {t('Cancel', 'Cancel')}
                    </button>
                    {editingId != null && can.delete && (
                      <button
                        type="button"
                        className="btn btn-danger"
                        onClick={() => void handleDelete()}
                        disabled={pending}
                      >
                        {t('Delete', 'Delete')}
                      </button>
                    )}
                    {editingId != null && can.edit && (
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={() => toggleActive.mutate(editingId)}
                        disabled={toggleActive.isPending}
                        title={t('Menus.ToggleActive', 'Toggle active')}
                      >
                        {form.isActive ? t('Menus.Disable', 'Disable') : t('Menus.Enable', 'Enable')}
                      </button>
                    )}
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
